import { inject, Injectable } from '@angular/core';
import { signalStore, withState, withMethods, } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, map, of, pipe, switchMap, tap } from 'rxjs';
import { ELoadingEntity, IErrorProperty } from 'src/models/generic-store.model';
import { withDevtools, withStorageSync, updateState } from '@angular-architects/ngrx-toolkit';
import { RestApiService } from 'src/services/rest-api.service';
import { IAppState, INotification } from 'src/models/app.model';
import { withError, withLoading, withoutError, withoutLoading, withRequestStatus } from '../store-features/with-request-status.store-feature';

enum EAppActions {
  HOTLINE_AVAILABILITY_REQUEST = '[App] Hotline Availability Request',
  HOTLINE_AVAILABILITY_SUCCESS = '[App] Hotline Availability Success',
  HOTLINE_AVAILABILITY_FAILURE = '[App] Hotline Availability Failure',
  COLLABORATOR_INFO_REQUEST = '[App] Collaborator Info Request',
  COLLABORATOR_INFO_SUCCESS = '[App] Collaborator Info Success',
  COLLABORATOR_INFO_FAILURE = '[App] Collaborator Info Failure',
  COLLABORATOR_PHOTO_REQUEST = '[App] Collaborator Photo Request',
  COLLABORATOR_PHOTO_SUCCESS = '[App] Collaborator Photo Success',
  COLLABORATOR_PHOTO_FAILURE = '[App] Collaborator Photo Failure',
  COLLABORATOR_RESET = '[App] Collaborator Reset',
  NOTIFICATION_SUM_REQUEST = '[App] Notification Sum Request',
  NOTIFICATION_SUM_SUCCESS = '[App] Notification Sum Success',
  NOTIFICATION_SUM_FAILURE = '[App] Notification Sum Failure',
  NOTIFICATION_DETAIL_SELECTED = '[App] Notification Detail Selected',
  CLEAR_NOTIFICATION_DETAIL_SELECTED = '[App] Clear Notification Detail Selected',
  SET_REDIRECT_URL = '[App] Set Redirect Url',
  CLEAR_REDIRECT_URL = '[App] Clear Redirect Url',
  DISPLAY_APP_IN_BACKGROUND_OVERLAY = '[App] Display App In Background Overlay',
  SET_DARK_MODE = '[App] Set Dark Mode',
  TOGGLE_HIDE_FINANCIAL_VALUES = '[App] Toggle Hide Financial Values',
  RESET_HIDE_FINANCIAL_VALUES = '[App] Reset Hide Financial Values',
}

const initialAppState: IAppState = {
  hotlineAvailability: null,
  hideFinancialValues: false,
  collaboratorInfo: null,
  collaboratorPhoto: null,
  unreadItemsSumCount: 0, // unreadNotificationsCount + unreadMessagesCount
  unreadNotificationsCount: 0,
  unreadMessagesCount: 0,
  selectedNotification: null,
  viewNeedUpdateOverlay: false,
};

const STORE_NAME = 'app';
@Injectable({ providedIn: 'root' })
export class AppStore extends signalStore(
  withDevtools(STORE_NAME),
  withState(initialAppState),
  withStorageSync({
    key: STORE_NAME + 'State',
    select: ({ hideFinancialValues, collaboratorInfo }) => ({ hideFinancialValues, collaboratorInfo })
  }),

  withRequestStatus(),
  withMethods((store, restApiService = inject(RestApiService)) => {

    const getHotlineAvailability = rxMethod<void>(
      pipe(
        tap(() =>
          updateState(store, EAppActions.HOTLINE_AVAILABILITY_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.HOTLINE_AVAILABILITY, loadingProps),
          }))
        ),
        switchMap(() =>
          restApiService.getHotlineAvailability().pipe(
            map(response =>
              updateState(store, EAppActions.HOTLINE_AVAILABILITY_SUCCESS, ({ loadingProps, errorProps }) => ({
                hotlineAvailability: response,
                ...withoutLoading(ELoadingEntity.HOTLINE_AVAILABILITY, loadingProps),
                ...withoutError(ELoadingEntity.HOTLINE_AVAILABILITY, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EAppActions.HOTLINE_AVAILABILITY_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.HOTLINE_AVAILABILITY, loadingProps),
                  ...withError(ELoadingEntity.HOTLINE_AVAILABILITY, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getCollaboratorInfo = rxMethod<void>(
      pipe(
        tap(() =>
          updateState(store, EAppActions.COLLABORATOR_INFO_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.COLLABORATOR_INFO, loadingProps),
          }))
        ),
        switchMap(() =>
          restApiService.getUserConsult().pipe(
            map(response =>
              updateState(store, EAppActions.COLLABORATOR_INFO_SUCCESS, ({ loadingProps, errorProps }) => ({
                collaboratorInfo: {
                  name: (response.name.titleBefore ? response.name.titleBefore + ' ' : '') + response.name.firstName,
                  surname: response.name.lastName + (response.name.titleAfter ? ' ' + response.name.titleAfter : ''),
                  phone: response.phone.prefix + response.phone.number,
                  email: response.email,
                },
                ...withoutLoading(ELoadingEntity.COLLABORATOR_INFO, loadingProps),
                ...withoutError(ELoadingEntity.COLLABORATOR_INFO, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EAppActions.COLLABORATOR_INFO_FAILURE, ({ loadingProps, errorProps }) => ({
                  collaboratorInfo: null,
                  ...withoutLoading(ELoadingEntity.COLLABORATOR_INFO, loadingProps),
                  ...withError(ELoadingEntity.COLLABORATOR_INFO, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getCollaboratorPhoto = rxMethod<void>(
      pipe(
        tap(() =>
          updateState(store, EAppActions.COLLABORATOR_PHOTO_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.COLLABORATOR_PHOTO, loadingProps),
          }))
        ),
        switchMap(() =>
          restApiService.getConsultantPhoto().pipe(
            map((nullableBlob) =>
              updateState(store, EAppActions.COLLABORATOR_PHOTO_SUCCESS, ({ loadingProps, errorProps }) => ({
                collaboratorPhoto: nullableBlob ? URL.createObjectURL(nullableBlob) : null,
                ...withoutLoading(ELoadingEntity.COLLABORATOR_PHOTO, loadingProps),
                ...withoutError(ELoadingEntity.COLLABORATOR_PHOTO, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EAppActions.COLLABORATOR_PHOTO_FAILURE, ({ loadingProps, errorProps }) => ({
                  collaboratorPhoto: null,
                  ...withoutLoading(ELoadingEntity.COLLABORATOR_PHOTO, loadingProps),
                  ...withError(ELoadingEntity.COLLABORATOR_PHOTO, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const resetCollaboratorState = () => {
      updateState(store, EAppActions.COLLABORATOR_RESET, ({ errorProps, loadingProps }) => ({
        collaboratorPhoto: null,
        collaboratorInfo: null,
        ...withoutLoading(ELoadingEntity.COLLABORATOR_PHOTO, loadingProps),
        ...withoutLoading(ELoadingEntity.COLLABORATOR_INFO, loadingProps),
        ...withoutError(ELoadingEntity.COLLABORATOR_PHOTO, errorProps),
        ...withoutError(ELoadingEntity.COLLABORATOR_INFO, errorProps),
      }));
    }

    const getNewNotificationsCount = rxMethod<void>(
      pipe(
        tap(() =>
          updateState(store, EAppActions.NOTIFICATION_SUM_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.NOTIFICATION_PROCESSED_SUM, loadingProps),
          }))
        ),
        switchMap(() =>
          restApiService.getNewNotificationsCount().pipe(
            map(res =>
              updateState(store, EAppActions.NOTIFICATION_SUM_SUCCESS, ({ loadingProps, errorProps }) => ({
                unreadItemsSumCount: res.totalSum,
                unreadMessagesCount: res.messageSum,
                unreadNotificationsCount: res.noticeSum,
                ...withoutLoading(ELoadingEntity.NOTIFICATION_PROCESSED_SUM, loadingProps),
                ...withoutError(ELoadingEntity.NOTIFICATION_PROCESSED_SUM, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EAppActions.NOTIFICATION_SUM_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.NOTIFICATION_PROCESSED_SUM, loadingProps),
                  ...withError(ELoadingEntity.NOTIFICATION_PROCESSED_SUM, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const toggleHideFinancialValues = () => {
      updateState(store, EAppActions.TOGGLE_HIDE_FINANCIAL_VALUES, ({ hideFinancialValues }) => ({
        hideFinancialValues: !hideFinancialValues,
      }));
    };

    const resetHideFinancialValues = () => {
      updateState(store, EAppActions.RESET_HIDE_FINANCIAL_VALUES, { hideFinancialValues: false });
    };

    const displayViewNeedUpdateOverlay = (flag: boolean) => {
      updateState(store, EAppActions.DISPLAY_APP_IN_BACKGROUND_OVERLAY, { viewNeedUpdateOverlay: flag });
    };

    const clearSelectedNotification = () => {
      updateState(store, EAppActions.CLEAR_NOTIFICATION_DETAIL_SELECTED, { selectedNotification: null });
    };

    const setSelectedNotification = (notification: INotification) => {
      updateState(store, EAppActions.NOTIFICATION_DETAIL_SELECTED, { selectedNotification: notification });
    };


    return {
      getHotlineAvailability,
      getCollaboratorInfo,
      getCollaboratorPhoto,
      resetCollaboratorState,
      getNewNotificationsCount,
      clearSelectedNotification,
      setSelectedNotification,
      displayViewNeedUpdateOverlay,
      toggleHideFinancialValues,
      resetHideFinancialValues,
    };
  })
) {}
